import { useRef, useState, useEffect, useCallback } from "react";
import { Gem, Star, Zap, Cherry, Trophy, Share2 } from "lucide-react";
import { toast } from "sonner";
import { sfxWin, sfxJackpot } from "@/lib/arcadeSound";
import { shareArcadeCard } from "@/lib/shareCard";
import Confetti from "@/components/arcade/Confetti";
import MarketScanner from "@/components/arcade/MarketScanner";
import HowToPlay from "@/components/arcade/HowToPlay";
import { useAutoRun, AutoRunBar } from "@/components/arcade/AutoRun";

const SYMBOLS = {
  seven: { render: (cls) => <span className={`${cls} font-black font-mono text-[#F7B731]`} style={{ fontSize: "1.6em", lineHeight: 1 }}>7</span> },
  gem: { render: (cls) => <Gem className={`${cls} text-[#2962FF]`} /> },
  star: { render: (cls) => <Star className={`${cls} text-white/70`} /> },
  zap: { render: (cls) => <Zap className={`${cls} text-[#089981]`} /> },
  cherry: { render: (cls) => <Cherry className={`${cls} text-[#F23645]`} /> },
};
const STRIP = ["seven", "gem", "star", "zap", "cherry", "gem", "seven", "star", "cherry", "zap"];
const OTHERS = ["gem", "star", "zap", "cherry"];

const Reel = ({ spinning, symbol, delayIdx }) => (
  <div className="relative w-20 h-24 rounded-lg border border-white/15 bg-[#0B0E14] overflow-hidden"
    style={{ boxShadow: "inset 0 8px 14px rgba(0,0,0,.7), inset 0 -8px 14px rgba(0,0,0,.7)" }}>
    {spinning ? (
      <div className="absolute inset-x-0" style={{ animation: `slot-spin .18s linear ${delayIdx * 0.05}s infinite` }}>
        {STRIP.map((s, i) => (
          <div key={i} className="h-24 flex items-center justify-center">{SYMBOLS[s].render("w-9 h-9")}</div>
        ))}
      </div>
    ) : (
      <div className="absolute inset-0 flex items-center justify-center" style={{ animation: "slot-stop .3s cubic-bezier(.2,1.4,.4,1)" }}>
        {SYMBOLS[symbol].render("w-10 h-10")}
      </div>
    )}
  </div>
);

/** Slot Machine — real Even/Odd contracts. Pick EVEN or ODD, reels spin
 * while the contract settles: win = triple 7s + celebration popup. Win 5
 * spins in a row for the JACKPOT (your real winnings + the badge). */
const StreakJackpot = ({ runFlip, busy, report, celebrate }) => {
  const [streak, setStreak] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem("vts_jackpot_best") || 0));
  const [pot, setPot] = useState(0);
  const [jackpot, setJackpot] = useState(false);
  const [winFx, setWinFx] = useState(null);   // {profit}
  const [lossFx, setLossFx] = useState(false);
  const [last, setLast] = useState(null);
  const [reels, setReels] = useState([{ spinning: false, symbol: "seven" }, { spinning: false, symbol: "seven" }, { spinning: false, symbol: "seven" }]);
  const timers = useRef([]);
  useEffect(() => {
    const t = timers.current;
    return () => t.forEach(clearTimeout);
  }, []);
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));
  const [mkt, setMkt] = useState(null);
  const symRef = useRef("1HZ100V");
  const mktRef = useRef(null);
  const onScan = useCallback((row) => { symRef.current = row.sym; mktRef.current = row; setMkt(row); }, []);
  const auto = useAutoRun("jackpot");

  const call = async (side) => {
    if (busy) return null;
    setWinFx(null);
    setLossFx(false);
    setReels([{ spinning: true }, { spinning: true }, { spinning: true }]);
    const res = await runFlip(side, null, "Slot Machine", symRef.current);
    if (!res) { setReels((r) => r.map(() => ({ spinning: false, symbol: "seven" }))); return null; }
    const ns = res.won ? streak + 1 : 0;
    const finals = res.won
      ? ["seven", "seven", "seven"]
      : ["seven", OTHERS[Math.floor(Math.random() * OTHERS.length)], OTHERS[Math.floor(Math.random() * OTHERS.length)]];
    finals.forEach((sym, i) => {
      later(() => {
        setReels((r) => r.map((x, j) => (j === i ? { spinning: false, symbol: sym } : x)));
        if (i === 2) {
          if (res.won) {
            sfxWin();
            if (ns < 5) {
              setWinFx({ profit: res.profit });
              later(() => setWinFx(null), 2400);
            }
          } else {
            setLossFx(true);
            later(() => setLossFx(false), 700);
          }
        }
      }, 250 + i * 300);
    });
    setLast(res);
    if (res.won) {
      const np = Math.round((pot + res.profit) * 100) / 100;
      setPot(np);
      report?.("slot_run", ns);
      if (ns >= 5) {
        later(() => { setJackpot(true); sfxJackpot(); }, 1100);
        celebrate?.("slot_jackpot", np);
        setStreak(0);
        setPot(0);
        if (5 > best) { setBest(5); localStorage.setItem("vts_jackpot_best", "5"); }
        later(() => setJackpot(false), 7000);
      } else {
        setStreak(ns);
        if (ns > best) { setBest(ns); localStorage.setItem("vts_jackpot_best", String(ns)); }
      }
    } else {
      setStreak(0);
      setPot(0);
    }
    return res;
  };

  const spinning = reels.some((r) => r.spinning);

  return (
    <div data-testid="jackpot-game" className="space-y-4">
      <HowToPlay id="jackpot"
        steps={[
          "The ⚡ Even/Odd Signal Engine scans all 10 markets for streaks and lopsided runs and locks the strongest — or says WAIT when everything is balanced.",
          "Tap ⚡ SPIN THE SIGNAL to take the detected side, or pick Even / Odd yourself.",
          "The reels spin while a real Deriv Even/Odd contract settles (~2s) — a win lands TRIPLE 7s and lights a jackpot bulb.",
          "Win 5 spins in a row for the JACKPOT celebration (your real winnings are already settled each spin).",
        ]}
        win="the next tick's last digit matches your pick — Even (0·2·4·6·8) or Odd (1·3·5·7·9) = triple 7s."
        payout="~×1.95 your stake per winning spin. The 5-bulb jackpot is the streak celebration — every win already paid out."
        tip="the jackpot meter is motivation, not a target — bank your session when you're up instead of forcing a 5-streak." />

      <MarketScanner id="jackpot" mode="evenodd" onChange={onScan}
        note="E/O = even vs odd share of the last 50 digits · streaks are history, every next tick stays ~50/50" />

      <style>{`
        @keyframes slot-spin {0%{transform:translateY(0)}100%{transform:translateY(-96px)}}
        @keyframes slot-stop {0%{transform:translateY(-30px);opacity:.4}100%{transform:translateY(0);opacity:1}}
        @keyframes sj-bulb {0%,100%{opacity:1}50%{opacity:.55}}
        @keyframes sj-jack {0%{transform:scale(.6)}55%{transform:scale(1.12)}100%{transform:scale(1)}}
        @keyframes sj-shake {0%,100%{transform:translateX(0)}25%{transform:translateX(-6px)}75%{transform:translateX(6px)}}
        @keyframes sj-pop {0%{transform:scale(.7);opacity:0}60%{transform:scale(1.08);opacity:1}100%{transform:scale(1)}}
      `}</style>

      <div className="relative rounded-xl border border-[#F7B731]/30 bg-[#131722] p-5 text-center space-y-4 overflow-hidden"
        style={{ boxShadow: "0 0 30px rgba(247,183,49,.08)" }}>
        {/* 💡 Vegas marquee lights */}
        <div className="flex justify-center gap-2" aria-hidden>
          {Array.from({ length: 14 }, (_, i) => (
            <span key={i} className="w-2 h-2 rounded-full"
              style={{ background: i % 2 ? "#F7B731" : "#F23645", animation: `sj-bulb ${spinning ? 0.35 : 1.2}s ease-in-out ${i * 0.08}s infinite`, boxShadow: `0 0 8px ${i % 2 ? "rgba(247,183,49,.7)" : "rgba(242,54,69,.6)"}` }} />
          ))}
        </div>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#F7B731]">VTS Slot Machine</p>

        {/* reels */}
        <div data-testid="jackpot-reels" className="flex justify-center gap-3" style={lossFx ? { animation: "sj-shake .35s ease 2" } : {}}>
          {reels.map((r, i) => <Reel key={i} spinning={r.spinning} symbol={r.symbol || "seven"} delayIdx={i} />)}
        </div>
        <p className={`text-[10px] font-mono ${lossFx ? "text-[#F23645]" : "text-white/45"}`}>
          {spinning ? "reels spinning — real contract settling…" : last ? (last.won ? "TRIPLE 7s — you hit it" : `no match — digit was ${last.digit}`) : "hit spin to roll the reels"}
        </p>

        {/* spin buttons */}
        <AutoRunBar id="jackpot" auto={auto} noun="spins" />
        {mkt?.signal && !mkt.signal.wait ? (
          <button data-testid="jackpot-signal-bet" disabled={busy || spinning || auto.active} onClick={() => {
            let last = mkt.signal.side;
            auto.burst(call, () => { const s = mktRef.current?.signal; if (s && !s.wait) last = s.side; return last; });
          }}
            className="w-full h-12 rounded-lg bg-[#089981] hover:brightness-110 text-white font-black text-sm uppercase tracking-wide transition disabled:opacity-40 active:scale-[0.99]">
            ⚡ Spin the signal — {mkt.signal.side} · {mkt.signal.strength}/100{auto.runs > 1 ? " · follows the best signal" : ""}
          </button>
        ) : (
          <div data-testid="jackpot-signal-wait" className="w-full h-12 rounded-lg border border-dashed border-white/15 grid place-items-center text-[10px] font-mono text-white/45">
            ⏳ no clear run right now — the engine re-scans every 12s
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <button data-testid="jackpot-even" disabled={busy || spinning || auto.active} onClick={() => auto.burst(call, "EVEN")}
            className="h-16 rounded-lg bg-[#F7B731] hover:bg-[#e0a41f] text-black transition-colors disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight">
            <span className="font-black text-sm uppercase tracking-wide">Spin · Even{mkt && mkt.evenPct >= 56 ? " 🔥" : ""}</span>
            <span className="text-[10px] font-mono text-black/70">wins on 0 · 2 · 4 · 6 · 8{mkt ? ` · recent E ${mkt.evenPct}%` : ""}</span>
          </button>
          <button data-testid="jackpot-odd" disabled={busy || spinning || auto.active} onClick={() => auto.burst(call, "ODD")}
            className="h-16 rounded-lg border border-[#F7B731]/60 bg-[#F7B731]/10 hover:bg-[#F7B731]/20 text-[#F7B731] transition-colors disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight">
            <span className="font-black text-sm uppercase tracking-wide">Spin · Odd{mkt && mkt.evenPct <= 44 ? " 🔥" : ""}</span>
            <span className="text-[10px] font-mono text-[#F7B731]/70">wins on 1 · 3 · 5 · 7 · 9{mkt ? ` · recent O ${100 - mkt.evenPct}%` : ""}</span>
          </button>
        </div>

        {/* jackpot progress */}
        <div className="pt-1">
          <p className="text-[9px] font-mono uppercase tracking-[0.25em] text-white/45 mb-2">Jackpot progress — win 5 spins in a row</p>
          <div data-testid="jackpot-meter" className="flex justify-center gap-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} data-testid={`streak-meter-bulb-${i + 1}`}
                className={`w-9 h-9 rounded-lg border flex items-center justify-center text-sm font-black font-mono transition-colors ${i < streak
                  ? "bg-[#F7B731] border-[#F7B731] text-black" : "bg-[#1A1E29] border-white/10 text-white/30"}`}
                style={i < streak ? { animation: "sj-bulb 1.1s ease-in-out infinite", boxShadow: "0 0 14px rgba(247,183,49,.5)" } : {}}>
                {i + 1}
              </div>
            ))}
          </div>
          <p className="text-[11px] font-mono text-white/60 mt-2">
            streak pot <span className="text-[#F7B731] font-bold">${pot.toFixed(2)}</span> · best run <span className="text-white font-bold">{best}</span>
          </p>
        </div>

        <p className="text-[9px] font-mono text-white/35">
          How it works: pick Even or Odd → reels spin on a real Deriv contract → a win shows triple 7s and lights a bulb · a miss resets the meter
        </p>

        {/* per-win celebration popup */}
        {winFx && !jackpot && (
          <div data-testid="jackpot-win-popup" className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-md">
            <Confetti count={36} />
            <div className="relative text-center px-8 py-5 rounded-xl border border-[#F7B731]/40 bg-black/50" style={{ animation: "sj-pop .4s cubic-bezier(.3,1.2,.4,1)" }}>
              <p className="text-4xl font-black tracking-tight text-[#F7B731]" style={{ textShadow: "0 0 24px rgba(247,183,49,.7)" }}>TRIPLE 7s!</p>
              <p className="mt-1 text-xl font-black font-mono text-[#089981]">+${winFx.profit.toFixed(2)}</p>
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50 mt-1">settled to your balance · bulb {Math.min(streak, 5)}/5 lit</p>
            </div>
          </div>
        )}

        {/* 5-streak jackpot overlay */}
        {jackpot && (
          <div data-testid="jackpot-win" className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-xl">
            <Confetti count={70} />
            <div className="relative text-center" style={{ animation: "sj-jack .8s cubic-bezier(.3,1.2,.4,1)" }}>
              <Trophy className="w-10 h-10 mx-auto text-[#F7B731]" style={{ filter: "drop-shadow(0 0 12px rgba(247,183,49,.8))" }} />
              <p className="text-4xl font-black tracking-tight text-[#F7B731]" style={{ textShadow: "0 0 30px rgba(247,183,49,.7)" }}>JACKPOT</p>
              <p className="text-[11px] font-mono text-white/60 mt-1">5 wins in a row — winnings already settled to your balance</p>
              <button data-testid="jackpot-share-btn"
                onClick={async () => {
                  const r = await shareArcadeCard({ title: "SLOT JACKPOT", value: "777", sub: "5 wins in a row · VTS Slot Machine" });
                  if (r === "downloaded") toast.success("Card saved to your device — share it anywhere");
                }}
                className="mt-3 inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-[#F7B731] hover:bg-[#e0a41f] text-black text-[11px] font-black uppercase tracking-wide transition-colors">
                <Share2 className="w-3.5 h-3.5" /> Share this
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StreakJackpot;
